import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { loadPinnedPaths, savePinnedPaths } from './useNavModel';

/**
 * Exactly one overlay may be open at a time. Each variant carries the viewport
 * anchor measured from its trigger's rect at open time — the sidebar and header
 * both clip (`overflow:hidden`), so every one of these is `position:fixed` and
 * cannot rely on an absolutely-positioned parent.
 */
export type ShellOverlay =
  | { kind: 'palette'; mode: 'nav' | 'as' }
  | { kind: 'notifications'; at: { top: number; right: number } }
  | { kind: 'region'; at: { top: number; right: number } }
  | { kind: 'profile'; at: { left: number; bottom: number } }
  | { kind: 'flyout'; groupId: string; top: number }
  | null;

interface ShellContextValue {
  rail: boolean;
  toggleRail: () => void;
  openGroups: Record<string, boolean>;
  toggleGroup: (groupId: string) => void;
  openGroup: (groupId: string) => void;
  pinnedPaths: string[];
  isPinned: (path: string) => boolean;
  togglePin: (path: string) => void;
  overlay: ShellOverlay;
  setOverlay: (overlay: ShellOverlay) => void;
  closeOverlay: () => void;
  /** Mobile drawer, which is a separate axis from the desktop rail. */
  mobileOpen: boolean;
  setMobileOpen: (open: boolean) => void;
}

const ShellContext = createContext<ShellContextValue | undefined>(undefined);

export const useShell = (): ShellContextValue => {
  const context = useContext(ShellContext);
  if (!context) {
    throw new Error('useShell must be used within a ShellProvider');
  }
  return context;
};

const RAIL_STORAGE_KEY = 'sidebar-collapsed';
const GROUPS_STORAGE_KEY = 'shell-groups-expanded';

const loadGroups = (): Record<string, boolean> => {
  if (typeof window === 'undefined') return {};
  const saved = window.localStorage.getItem(GROUPS_STORAGE_KEY);
  if (!saved) return {};
  try {
    const parsed = JSON.parse(saved);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

export const ShellProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [rail, setRail] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(RAIL_STORAGE_KEY) === 'true';
  });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(loadGroups);
  const [pinnedPaths, setPinnedPaths] = useState<string[]>(loadPinnedPaths);
  const [overlay, setOverlayState] = useState<ShellOverlay>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleRail = useCallback(() => {
    setRail((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(RAIL_STORAGE_KEY, String(next));
      }
      return next;
    });
    // Collapsing the sidebar moves every anchor the open overlay was measured from.
    setOverlayState(null);
  }, []);

  const persistGroups = useCallback((next: Record<string, boolean>) => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(GROUPS_STORAGE_KEY, JSON.stringify(next));
    }
    return next;
  }, []);

  // Accordion: expanding a group collapses its siblings, so a long admin tree
  // never grows past the viewport.
  const toggleGroup = useCallback(
    (groupId: string) => {
      setOpenGroups((prev) => persistGroups({ [groupId]: !prev[groupId] }));
    },
    [persistGroups],
  );

  const openGroup = useCallback(
    (groupId: string) => {
      setOpenGroups((prev) => (prev[groupId] ? prev : persistGroups({ [groupId]: true })));
    },
    [persistGroups],
  );

  const togglePin = useCallback((path: string) => {
    setPinnedPaths((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
      savePinnedPaths(next);
      return next;
    });
  }, []);

  const isPinned = useCallback((path: string) => pinnedPaths.includes(path), [pinnedPaths]);

  const closeOverlay = useCallback(() => setOverlayState(null), []);

  const setOverlay = useCallback((next: ShellOverlay) => {
    setOverlayState((prev) => {
      if (!prev || !next || prev.kind !== next.kind) return next;
      // Re-triggering the open overlay closes it — except for the palette,
      // where the same kind in the other mode is a switch, not a toggle.
      if (prev.kind === 'palette' && next.kind === 'palette' && prev.mode !== next.mode) {
        return next;
      }
      if (prev.kind === 'flyout' && next.kind === 'flyout' && prev.groupId !== next.groupId) {
        return next;
      }
      return null;
    });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOverlayState(null);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const value = useMemo<ShellContextValue>(
    () => ({
      rail,
      toggleRail,
      openGroups,
      toggleGroup,
      openGroup,
      pinnedPaths,
      isPinned,
      togglePin,
      overlay,
      setOverlay,
      closeOverlay,
      mobileOpen,
      setMobileOpen,
    }),
    [
      rail,
      toggleRail,
      openGroups,
      toggleGroup,
      openGroup,
      pinnedPaths,
      isPinned,
      togglePin,
      overlay,
      setOverlay,
      closeOverlay,
      mobileOpen,
    ],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
};
